import React from 'react'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import DualActionButtons from '../../components/action_button/DualActionButtons'
import GuidanceNote from '../../components/GuidanceNote'
import {
  dialogActionButtonSx,
  dialogActionsSx,
  dialogAvatarSx,
  dialogBodyTextSx,
  dialogConfirmUserCardSx,
  dialogContentSx,
  dialogDividerCompactSx,
  dialogHeaderSx,
  dialogMetaTextSx,
  dialogTitleSx,
  getDialogCancelAction,
  getDialogPaperSx,
  guidanceNoteIconSx,
  guidanceNoteWarningColors,
  guidanceNoteWarningHeaderSx,
  guidanceNoteWarningMessageSx,
  guidanceNoteWarningSx
} from '../../constants/dialogSx'
import { UI_COLOR_PRIMARY, UI_COLOR_PRIMARY_HOVER } from '../../constants/uiColors'
import { RBAC_USER_DETAIL_TEXTS } from '../pages/shared/rbacUserDetailConstants'
import {
  getRbacUserDisplayName,
  getRbacUserInitials,
  getRbacUserPrimaryRoleName
} from '../pages/shared/rbacUserDetailHelpers'

const dialogPaperSx = getDialogPaperSx(520)

const confirmNameSx = {
  fontWeight: 700,
  color: dialogTitleSx.color,
  lineHeight: 1.3
}

export default function RbacUserConfirmDialog({
  open,
  onClose,
  onConfirm,
  onExited,
  user,
  title,
  notice,
  body,
  confirmLabel,
  confirmColor = UI_COLOR_PRIMARY,
  confirmHoverColor = UI_COLOR_PRIMARY_HOVER,
  submitting = false,
  loadingLabel
}) {
  const displayName = getRbacUserDisplayName(user)
  const roleName = getRbacUserPrimaryRoleName(user)

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      TransitionProps={{ onExited }}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogHeaderSx}>
        <Typography component="span" sx={dialogTitleSx}>
          {title}
        </Typography>
      </DialogTitle>
      <Divider sx={dialogDividerCompactSx} />
      <DialogContent sx={dialogContentSx}>
        {notice ? (
          <GuidanceNote
            icon={<WarningAmberOutlinedIcon />}
            title=""
            message={notice}
            accentColor={guidanceNoteWarningColors.accentColor}
            backgroundColor={guidanceNoteWarningColors.backgroundColor}
            textColor={guidanceNoteWarningColors.textColor}
            singleLine
            sx={guidanceNoteWarningSx}
            headerSx={guidanceNoteWarningHeaderSx}
            iconSx={guidanceNoteIconSx}
            messageSx={guidanceNoteWarningMessageSx}
          />
        ) : null}

        {body ? <Typography sx={dialogBodyTextSx}>{body}</Typography> : null}

        <Box sx={dialogConfirmUserCardSx}>
          <Avatar sx={dialogAvatarSx}>{getRbacUserInitials(displayName)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={confirmNameSx}>{displayName}</Typography>
            <Typography sx={dialogMetaTextSx}>
              {user?.email || '—'} - Rol: {roleName}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <DualActionButtons
          fullWidthOnMobile={false}
          minWidth={112}
          gap={1}
          secondaryAction={getDialogCancelAction({
            label: RBAC_USER_DETAIL_TEXTS.cancel,
            onClick: onClose,
            disabled: submitting,
            sx: dialogActionButtonSx
          })}
          primaryAction={{
            label: confirmLabel,
            loadingLabel,
            showLoadingIcon: false,
            icon: null,
            color: confirmColor,
            hoverColor: confirmHoverColor,
            onClick: onConfirm,
            disabled: submitting,
            loading: submitting,
            sx: dialogActionButtonSx
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
